import { Interactable } from "@interactjs/types/index";
import { Field } from "./field.model";
import { Squad, SquadPlayer } from "./squad.model";
import { IStats, UbikoStats } from "./ubiko-stats.model";
import { ElementRef } from "@angular/core";
import { Kit } from "./kit.model";
import { PlayerInjuryRequest } from "./injury.model";

export class Session {

    public static STARTED = 'started';
    public static COMPUTED = 'computed';
    public static FINISHED = 'finished';
    public static readonly TYPE_MATCH = 'match';
    public static readonly TYPE_MATCH_TRAINING = 'match_training';
    public static readonly TYPE_TRAINING = 'training';
    public static readonly GROUP_REHABILITATION = 'rehabilitation';

    id: string;
    name: string; 
    created: string;  
    finished: string;
    status: string;
    squad: Squad;
    score?: Score;
    params?: any;
    typeInfo: TypeInfo;
    training_type: string;
    field: Field;
    ubiko_stats: UbikoStats;
    parts?: Part[];
    rpe?: SessionRPEMap;
    calendar?: SessionCalendarLink;
    kit: SessionKit;
    _devices: any;
    source: string;
    live_preview?: boolean;
    constructor() {}      
}

export interface Part {
    id: string;
    name: string; 
    type?: string | null;
    start: string;
    end: string;
    status: string;
    ubiko_stats?: IStats;
    _players: PartPlayer[];
    players: any;
    //stats: IStats;   
    events: any[];
    group?: string;
}

export interface PartPlayer {
    id: string;
    ubiko_stats: IStats;    
    start: string;
    end: string;
    device?: string;
    data: {
        heatmap?: string;
    }
    clone?: string;
}

export interface SessionRPE {
    rpe?: number;
}

export interface SessionRPEMap {
    [id_player: string]: SessionRPE;
}

export interface SessionCalendarLink {
    eventId: string;
    occurrenceKey: string;
    occurrenceStart?: string;
    linkedAt?: string;
}

export interface TypeInfo {
    name: string;
    type: string;
}

export interface SessionKit {
    id: string;
    type: string;
    name?: string
}

export interface Score {
    goals: number;
    opponent_goals: number;
}

export interface SessionOpponentValue {
    name?: unknown;
    shield?: unknown;
}

export interface NormalizedSessionOpponent {
    name: string;
    shield: string;
}

/**
 * Supports the current API contract, the former string value and documents
 * affected by the historical `{ name: { name, shield }, shield }` nesting bug.
 */
export function normalizeSessionOpponent(
    opponent: unknown,
    fallbackShield = '',
): NormalizedSessionOpponent {
    let current = opponent;
    let shield = '';
    const visited = new Set<object>();

    // A finite limit also protects rendering code from hostile or malformed
    // payloads containing an excessively deep chain of `name` objects.
    for(let depth = 0; depth < 16; depth++) {
        if(typeof current === 'string') {
            return {
                name: current,
                shield: shield || fallbackShield || '',
            };
        }

        if(!current || typeof current !== 'object' || visited.has(current)) {
            break;
        }

        visited.add(current);
        const value = current as SessionOpponentValue;
        if(!shield && typeof value.shield === 'string' && value.shield) {
            shield = value.shield;
        }
        current = value.name;
    }

    return {
        name: '',
        shield: shield || fallbackShield || '',
    };
}

/** Normalizes API/Firestore data in place at shared session read boundaries. */
export function normalizeSessionOpponentParams(session: Session): Session {
    const params = session?.params;
    if(params && params.opponent !== undefined && params.opponent !== null) {
        const legacyShield = typeof params.opponent_shield === 'string'
            ? params.opponent_shield
            : '';
        session.params = {
            ...params,
            opponent: normalizeSessionOpponent(params.opponent, legacyShield),
        };
    }
    return session;
}

export function completeSession(session: Session, completeParts?: boolean) {
    normalizeSessionOpponentParams(session);
    if(session.ubiko_stats) {
        session.ubiko_stats._players = [];
        if(session.ubiko_stats.players) {
            for (let [key, value] of Object.entries(session.ubiko_stats.players)) {        
                let stats = value as IStats;
                stats.id_player = key;
                session.ubiko_stats._players.push(stats);
            }
        }
    }
    //session._parts = [];
    if(completeParts && session.parts) {        
        session.parts.forEach(part => {   
            part._players = [];
            for (let value of Object.values(part.players)) {  
                part._players.push(value as PartPlayer);
            }
        });
    }
}

export interface SessionPlayer {   
    player: SquadPlayer; 
    id_player: string;
    group: string;
    availability: string;
    previewSpeeds?: PreviewSpeed;
    parts?: PartBuilder[];
    class?: string;
    start?: Date;
    end?: Date;
    device?: DeviceInfo;
    chart?: DeviceChart, 
    sectionEl?: ElementRef;   
    inSession?: boolean;
}

export interface SessionGlobal {    
    name: string;    
    start: Date;
    end: Date;    
    parts: PartBuilder[];    
    chart?: DeviceChart,
    class?: string;  
    speeds?: SpeedInstant[];
}

export interface Speed {
    value: number;
    timestamp: number;
}

export interface PreviewSpeed {
    max: number;
    speeds: Speed[];
}

export interface SpeedPoint {
    value: number | string;
    position: number;
}

export interface SpeedInstant {
    winStart: number;
    winEnd: number;
    timestamp: number;
    speeds: number[];
    average: number;
}

interface DeviceChart {
    x_axis: SpeedPoint[];
    y_axis: SpeedPoint[];
    points: string[];
    initialized?: boolean;
}

export interface DeviceInfo {
    id: string;
    players?: SquadPlayer[];    
    name: string;
    completed: boolean;
    start?: Date;
    end?: Date; 
    preview?: Preview;
    disabled?: boolean;
}

export interface DeviceAssignment {    
    id_player: string;
    id_device?: string;   
    group: string;
    availability: string;
    /** Active injury reference. It remains set until availability is `available`. */
    injury_id?: string;
}

export interface SessionGroup {
    group: ConfigGroup;
    global?: SessionGlobal;
    selected?: boolean;
}

export interface ConfigGroup {
    id: string;
    name: string;    
    order?: number;
    default?: boolean;    
}

export interface ConfigTaskType {
    id: string;
    name: string; 
    order?: number;
    default?: boolean;    
}

export interface ConfigResponse {
    [id: string]: ConfigEntry;
}

export interface ConfigEntry {
    order?: number;
    default?: boolean;
    translations: {
        [lang: string]: ConfigTranslation;
    };
}

export interface ConfigTranslation {
    name: string;
    description: string;
}

export interface ConfigAvailability {
    id: string;
    name: string;
    description: string;
    order?: number;
    default?: boolean;
}

export interface ConfigTrainingType {
    id: string;
    name: string;   
    order?: number;
    default?: boolean;
}

export interface TabState {
    active: SessionTab;
    previous?: SessionTab;    
}

export interface SessionTab {
    name: string;
    template: string;
    icon: string;
    loaded?: boolean;
    active?: boolean;
    display?: boolean;
    disabled?: boolean;
    completed?: boolean;
    warning?: string;
    userConfirmed?: boolean;
}

export enum TabNames {
    kit = 'kit',
    upload = 'upload',
    data = 'data',
    players = 'players',
    tasks = 'tasks'
}

export interface BuilderEvent {
    subscription?: {
        players?: boolean;
        tasks?: boolean;
    }    
    detectChanges?: boolean;
}

export type PreprocessedReloadMode = false | 'cache' | 'force';

export interface BuilderState {  
    changes: {
        data: boolean;
        players: boolean;
        tasks: boolean;
    }
    reload: {
        players: string[];      
        preprocessed: PreprocessedReloadMode;
    }
    errors: {
        data: string[];
        players: string[];
        tasks: string[];
    }    
    warnings: {
        data: string[];
        players: string[];
        tasks: string[];
    }  
}

export interface SessionRequest { 
    created: Date;
    finished: Date;
    type?: string;
    calendar?: SessionCalendarLink;
    rpe?: SessionRPEMap;
    field?: string;
    kitSelected?: Kit;
    kit: string;
    name?: string;
    squad: string;
    status?: string;
    params?: {
        rpe: number;
        opponent?: NormalizedSessionOpponent;
    };
    training_type?: string;
    score?: {
        goals: number;
        opponent_goals: number;
    }
    assignments?: DeviceAssignment[];
    injuries?: PlayerInjuryRequest[];
    autocompute?: boolean;
    events?: SessionEvent[];
    parts?: PartRequest[]; // TODO
}

export interface PartRequest {   
    _id_part: string; 
    start: string;
    end: string;
    name: string;
    type?: string | null;
    group: string;
    players: PartPlayerRequest[];
    id?: string;    
}

export interface PartPlayerRequest {
    id: string;
    start: string;
    end: string;
    device: string;
}

export interface SessionEvent {
    id: string;
    player: string;
    player2: string;
    minute: number;
    instant: string;
    playerData: any;
    player2Data: any;
}

export interface PartBuilder {
    id_part: string;    
    group: string;
    startStep: number;
    endStep: number;
    start: string;
    end: string;
    duration: string;
    width: number;
    left: number;
    name: string;
    type?: string | null;
    editMode?: boolean,    
    new?: boolean;
    interactable: Interactable | undefined;
}

export interface PartRestrinction {
    id_part: string;
    left: number;
    right: number;
}

export interface ResizableResponse {
    interactable: Interactable;
    /*results: {
        periodsChanged: boolean;
        currentlyDragged: boolean;
    }*/
}

export interface ResizableEvent {    
    id_part: string;
    //global: {
    //    start: Date;
    //    end: Date;        
    //}
    //players: ElementState[];
    applyRestrinctions?: boolean;
}

export interface ElementState {    
    id: string;
    start: Date;
    end: Date;    
}

export interface Preview {
    device: {
        id: string;
        name: string;
    }
    data: PreviewData[];
    player?: {
        id: string;
    }
}

export interface PreviewData {
    t: number;
    s: number;
}

export interface TimeWindow {
    start: Date;
    end: Date;
    duration: number;
    speedAvg?: number;  
}

export interface PreviewDeviceStatus {
    id: string;
    completed: boolean;
}

export interface InvalidPart {
    name: string; 
    type: string;
}

export interface WeekDay {
    day: number;
    name: string;
    date: {
        d: number;
        m: number; 
        y: number;
    },
    active: boolean;
}

export interface SessionEvent {
    id: string;
    player: string;
    player2: string;
    opponent: string;
    opponent2: string;
    type: string;
    instant: string;
    minute: number;
}
